import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { apiPaths } from '../../../service/apiPaths';
import ApiService from '../../../service/ApiService';
import { formatDateToISODateString, limit } from '../../../util/constant';

interface Contact {
  _id: string;
  userId?: {
    name: string;
  };
  name: string;
  description?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  platform?: {
    name: string;
  };
  interest?: {
    type: string;
  };
  services?: {
    type: string;
  };
  outputStatus?: {
    status: string;
  };
  linkedin?: string;
  createdAt?: string;
  status: "Active" | "InActive";
}

const useContact = () => {
  const [contactData, setContactData] = useState<Contact[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showFollowupModal, setShowFollowupModal] = useState<boolean>(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const navigate = useNavigate();

  const fetchContacts = async (
    searchTerm?: string,
    currentPage?: number,
    fromDate?: Date | null,
    toDate?: Date | null
  ) => {
    setLoading(true);
    try {
      let endpoint = `${apiPaths.getContact}?page=${currentPage || page}&limit=${limit}`;
      
      if (searchTerm || search) {
        const searchValue = searchTerm || search;
        endpoint += `&search=${searchValue}`;
      }
      
      if (fromDate && toDate) {
        const formattedFromDate = formatDateToISODateString(fromDate);
        const formattedToDate = formatDateToISODateString(toDate);
        endpoint += `&fromDate=${formattedFromDate}&toDate=${formattedToDate}`;
      }

      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      console.log("result",result);
      
      setContactData(result?.contacts || []);
      setTotalPage(result?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useEffect triggered - page:', page, 'search:', search);
    fetchContacts();
  }, [page, search]);

  // Prevent unnecessary re-renders by memoizing the search handler
  const memoizedHandleSearch = useCallback((searchTerm: string) => {
    console.log('Search term received:', searchTerm);
    handleSearch(searchTerm);
  }, []);

  const handleDateFilter = async (fromDate: Date | null, toDate: Date | null) => {
    await fetchContacts(search, page, fromDate, toDate);
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchTerm: string) => {
        console.log('Debouncing search:', searchTerm);
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          console.log('Executing search:', searchTerm);
          setSearch(searchTerm);
          setPage(1);
        }, 500); // 500ms delay - more reasonable
      };
    })(),
    []
  );

  const handleSearch = (searchTerm: string) => {
    debouncedSearch(searchTerm);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleEdit = (id: string) => {
    navigate(`/edit-contact/${id}`);
  };

  const handleDelete = (id: string) => {
    setDeletingId(id);
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    
    setLoading(true);
    try {
      await ApiService({
        method: "DELETE",
        endpoint: `${apiPaths.deleteContact}${deletingId}`,
      });
      
      setContactData((prevData) =>
        prevData.filter((contact) => contact._id !== deletingId)
      );
      
      toast.success("Contact deleted successfully");
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("Failed to delete contact");
    } finally {
      setLoading(false);
    }
    
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleCancelDelete = () => {
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleAdd = () => {
    navigate("/addContact");
  };

  const handleEmail = (id: string) => {
    // Implement email functionality
    console.log("Email contact:", id);
  };

  const handleFollowup = (contact: Contact) => {
    setSelectedContact(contact);
    setShowFollowupModal(true);
  };

  const handleCloseFollowupModal = () => {
    setShowFollowupModal(false);
    setSelectedContact(null);
  };

  const handleStatusUpdate = async (id: string) => {
    setLoading(true);
    try {
      const contact = contactData.find((c) => c._id === id);
      const newStatus = contact?.status === "Active" ? "InActive" : "Active";
      
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.editContact}${id}`,
        data: { status: newStatus },
      });
      
      setContactData((prevData) =>
        prevData.map((contact) =>
          contact._id === id
            ? { ...contact, status: newStatus }
            : contact
        )
      );
      
      toast.success("Status updated successfully");
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  return {
    contactData,
    loading,
    search,
    page,
    totalPage,
    showConfirmation,
    showFollowupModal,
    selectedContact,
    handleSearch: memoizedHandleSearch,
    handlePageChange,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    handleAdd,
    handleEmail,
    handleFollowup,
    handleCloseFollowupModal,
    handleStatusUpdate,
    handleDateFilter,
    fetchContacts,
  };
};

export default useContact; 